import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../api/client.js';
import CartDrawer from '../components/CartDrawer.jsx';
import { ClockIcon, FlameIcon, SearchIcon, DotIcon } from '../components/Icons.jsx';
import PdfViewer from '../components/PdfViewer.jsx';
import MenuAd from '../components/MenuAd.jsx';

// AdSense slot IDs from build-time env (must be VITE_ prefixed)
const AD_SLOT_HEADER = import.meta.env.VITE_ADSENSE_SLOT_HEADER || '';
const AD_SLOT_FOOTER = import.meta.env.VITE_ADSENSE_SLOT_FOOTER || '';

const getText = (val, locale, fallback = '') => {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  return val[locale] || val.en || val.ar || Object.values(val)[0] || fallback;
};

const adjustColor = (hex, percent) => {
  if (!hex || !hex.startsWith('#')) return hex;
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + percent));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + percent));
  const b = Math.max(0, Math.min(255, (num & 0x0000ff) + percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

// Day keys (Mon-first to match HoursTab)
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Compute current open status based on opening_hours JSON
function getOpenStatus(openingHours) {
  if (!openingHours) return null;
  const now = new Date();
  const dayIdx = (now.getDay() + 6) % 7; // Monday = 0
  const todayKey = DAY_KEYS[dayIdx];
  const today = openingHours[todayKey];

  if (!today || !today.open) {
    return { isOpen: false, label: 'Closed today', todayHours: null };
  }

  const fromMin = parseTime(today.from);
  const toMin = parseTime(today.to);
  const nowMin = now.getHours() * 60 + now.getMinutes();

  let isOpen;
  if (fromMin == null || toMin == null) {
    isOpen = false;
  } else if (toMin > fromMin) {
    isOpen = nowMin >= fromMin && nowMin <= toMin;
  } else {
    // Overnight (e.g. 18:00 → 02:00)
    isOpen = nowMin >= fromMin || nowMin <= toMin;
  }

  return {
    isOpen,
    label: isOpen ? 'Open now' : 'Closed',
    todayHours: `${today.from} – ${today.to}`,
  };
}

function parseTime(t) {
  if (!t || typeof t !== 'string') return null;
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

// Glassmorphism button style for header icons
const glassButtonStyle = {
  width: '32px', height: '32px',
  background: 'rgba(255,255,255,0.18)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.28)',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background 0.15s',
};

// 24-character random hex ID for anonymous visitor tracking.
// Uses crypto.randomUUID when available (HTTPS / localhost), otherwise falls
// back to crypto.getRandomValues, otherwise to Math.random — good enough for
// non-security-sensitive analytics IDs.
function generateVisitorId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '').slice(0, 24);
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(12);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch { /* fall through */ }
  // Last resort
  let id = '';
  while (id.length < 24) id += Math.random().toString(16).slice(2);
  return id.slice(0, 24);
}

export default function PublicMenuPage() {
  const { slug } = useParams();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locale, setLocale] = useState('en');
  const [search, setSearch] = useState('');
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const categoriesRef = useRef(null);

  // Cart state — persisted to localStorage per menu slug
  const cartStorageKey = `almenu_cart_${slug}`;
  const [cart, setCart] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(`almenu_cart_${slug}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [showCart, setShowCart] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (cart.length === 0) {
      localStorage.removeItem(cartStorageKey);
    } else {
      try {
        localStorage.setItem(cartStorageKey, JSON.stringify(cart));
      } catch {
        // Storage may be full or disabled — ignore silently
      }
    }
  }, [cart, cartStorageKey]);

  const cartTotal = useMemo(() =>
    cart.reduce((sum, it) => sum + (it.price * it.quantity), 0)
  , [cart]);
  const cartCount = useMemo(() =>
    cart.reduce((sum, it) => sum + it.quantity, 0)
  , [cart]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(it => it.itemId === item.id);
      if (existing) {
        return prev.map(it =>
          it.itemId === item.id ? { ...it, quantity: it.quantity + 1 } : it
        );
      }
      return [...prev, {
        itemId: item.id,
        name: item.name,
        price: parseFloat(item.price) || 0,
        quantity: 1,
      }];
    });
  };

  const updateCartQty = (itemId, newQty) => {
    if (newQty <= 0) {
      setCart(prev => prev.filter(it => it.itemId !== itemId));
    } else {
      setCart(prev => prev.map(it => it.itemId === itemId ? { ...it, quantity: newQty } : it));
    }
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(it => it.itemId !== itemId));
  };

  const clearCart = () => setCart([]);

  // Get or create persistent visitor ID (anonymous, in localStorage).
  // crypto.randomUUID is only available in secure contexts (HTTPS / localhost),
  // so we fall back to a Math.random-based generator over plain HTTP dev hosts.
  const visitorId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    let id = localStorage.getItem('almenu_visitor_id');
    if (!id) {
      id = generateVisitorId();
      localStorage.setItem('almenu_visitor_id', id);
    }
    return id;
  }, []);

  useEffect(() => {
    publicApi.getMenu(slug, visitorId)
      .then((m) => {
        setMenu(m);
        setLocale(m.default_locale || 'en');
        if (m.sections?.[0]) setActiveSectionId(m.sections[0].id);

        // Sync cart with the latest menu: drop items that were removed/disabled,
        // refresh names + prices for items that still exist.
        const itemMap = new Map();
        (m.sections || []).forEach(s => {
          (s.items || []).forEach(it => {
            if (it.isAvailable !== false) itemMap.set(it.id, it);
          });
        });
        setCart(prev => {
          if (prev.length === 0) return prev;
          const next = prev
            .filter(ci => itemMap.has(ci.itemId))
            .map(ci => {
              const fresh = itemMap.get(ci.itemId);
              return {
                ...ci,
                name: fresh.name,
                price: parseFloat(fresh.price) || 0,
              };
            });
          return next;
        });
      })
      .catch(err => setError(err.response?.data?.error || 'Menu not found'))
      .finally(() => setLoading(false));

    publicApi.trackScan(slug).catch(() => {});
  }, [slug, visitorId]);

  // Toggle like (optimistic update)
  const handleToggleLike = async (itemId) => {
    if (!visitorId) return;

    // Optimistic local update
    setMenu(prev => {
      if (!prev?.sections) return prev;
      const newSections = prev.sections.map(s => ({
        ...s,
        items: (s.items || []).map(it => {
          if (it.id !== itemId) return it;
          const wasLiked = it.isLiked;
          return {
            ...it,
            isLiked: !wasLiked,
            likesCount: Math.max(0, (it.likesCount || 0) + (wasLiked ? -1 : 1)),
          };
        }),
      }));
      return { ...prev, sections: newSections };
    });

    try {
      await publicApi.toggleLike(itemId, visitorId);
    } catch (err) {
      // Revert on failure
      setMenu(prev => {
        if (!prev?.sections) return prev;
        const newSections = prev.sections.map(s => ({
          ...s,
          items: (s.items || []).map(it => {
            if (it.id !== itemId) return it;
            const wasLiked = it.isLiked;
            return {
              ...it,
              isLiked: !wasLiked,
              likesCount: Math.max(0, (it.likesCount || 0) + (wasLiked ? -1 : 1)),
            };
          }),
        }));
        return { ...prev, sections: newSections };
      });
    }
  };

  // Compute "popular" item per section (most liked)
  const popularItemIds = useMemo(() => {
    if (!menu?.sections) return new Set();
    const ids = new Set();
    menu.sections.forEach(s => {
      const items = s.items || [];
      if (items.length === 0) return;
      const top = items
        .filter(it => (it.likesCount || 0) >= 3)
        .sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0))[0];
      if (top) ids.add(top.id);
    });
    return ids;
  }, [menu]);

  // Scroll listener: show "back to top" + auto-detect active section
  useEffect(() => {
    if (!menu?.sections) return;

    const handleScroll = () => {
      // Show back-to-top button after 400px scroll
      setShowScrollTop(window.scrollY > 400);

      // Detect active section based on scroll position
      const offset = 140; // sticky categories bar height + buffer
      let currentId = menu.sections[0]?.id;
      for (const section of menu.sections) {
        const el = document.getElementById(`sec-${section.id}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= offset) {
          currentId = section.id;
        } else {
          break;
        }
      }
      setActiveSectionId(currentId);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menu]);

  // Reflect the restaurant name in the browser tab title (and reset on unmount).
  useEffect(() => {
    if (!menu) return;
    const name = getText(menu.name, locale, '');
    const previous = document.title;
    document.title = name ? `${name} — Menu` : 'Menu';
    return () => { document.title = previous; };
  }, [menu, locale]);

    // Scroll active category pill into view — horizontally inside the pills bar
  // ONLY. We avoid scrollIntoView() because it can also scroll the page
  // vertically (yanking the user back up while they're reading).
  useEffect(() => {
    if (!activeSectionId || !categoriesRef.current) return;
    const container = categoriesRef.current;
    const activeBtn = container.querySelector(`[data-section-id="${activeSectionId}"]`);
    if (!activeBtn) return;
    const target = activeBtn.offsetLeft - (container.clientWidth - activeBtn.offsetWidth) / 2;
    container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [activeSectionId]);

  // Smooth scroll with offset for sticky header
  const scrollToSection = (sectionId) => {
    const el = document.getElementById(`sec-${sectionId}`);
    if (!el) return;
    const offset = 110; // sticky categories bar
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredSections = useMemo(() => {
    if (!menu?.sections) return [];
    const term = search.trim().toLowerCase();
    if (!term) return menu.sections;

    return menu.sections
      .map(s => ({
        ...s,
        items: (s.items || []).filter(it => {
          const name = getText(it.name, locale).toLowerCase();
          const desc = getText(it.description, locale).toLowerCase();
          return name.includes(term) || desc.includes(term);
        }),
      }))
      .filter(s => s.items.length > 0);
  }, [menu, search, locale]);

  if (loading) return <div className="loading">Loading menu...</div>;
  if (error) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '12px',
        background: '#FAFAFB', padding: '20px',
      }}>
        <div style={{ fontSize: '18px', fontWeight: 600 }}>Menu not found</div>
        <div style={{ fontSize: '13px', color: '#94A3B8' }}>{error}</div>
      </div>
    );
  }
  if (!menu) return null;

  // Custom styles from legacy migration (sectionBgColor, headerColor, etc.)
  const customStyles = menu.custom_styles || {};
  // Pick the brand color: old "headerColor" overrides primary_color when present
  // (legacy primary_color was often '#000000' while the actual brand color lived in headerColor).
  const primaryColor = customStyles.headerColor || menu.primary_color || '#7C3AED';
  const primaryDark = adjustColor(primaryColor, -40);
  // Accent / price color — from old "bodyColor" if customised, else falls back to primary
  const accentColor = customStyles.bodyColor && customStyles.bodyColor !== primaryColor
    ? customStyles.bodyColor
    : primaryColor;
  // Section header customisation
  const sectionBg = customStyles.sectionBgColor || null;
  const sectionFg = customStyles.sectionTextColor || null;

  // Normalize font: prefer custom_styles.typographyHeader, then font_family, then default
  const normalizeFont = (raw) => {
    const f = (raw || '').trim();
    if (!f) return null;
    if (f.includes(',') || f.includes('serif') || f.includes('sans')) return f;
    const wrapped = /\s/.test(f) ? `"${f}"` : f;
    return `${wrapped}, Rubik, Inter, -apple-system, sans-serif`;
  };
  const fontFamily =
    normalizeFont(customStyles.typographyBody) ||
    normalizeFont(menu.font_family) ||
    'Rubik, Inter, -apple-system, sans-serif';
  const headerFontFamily =
    normalizeFont(customStyles.typographyHeader) ||
    fontFamily;
  const restaurantName = getText(menu.name, locale, 'Restaurant');
  const tagline = getText(menu.tagline, locale, '');
  const isRtl = locale === 'ar';
  const supportedLocales = menu.supported_locales || [menu.default_locale || 'en'];
  const contactInfo = menu.contact_info || {};
  const hasContact = Object.keys(contactInfo).length > 0;
  const openStatus = getOpenStatus(menu.opening_hours);

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      style={{
        minHeight: '100vh',
        background: '#FAFAFB',
        fontFamily,
      }}
    >
      <div style={{ maxWidth: '480px', margin: '0 auto', background: 'white', minHeight: '100vh', position: 'relative' }}>
        {/* Cover */}
        <div style={{
          height: menu.cover_url ? '180px' : '130px',
          background: menu.cover_url
            ? `center/cover url("${menu.cover_url}")`
            : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
          position: 'relative',
          padding: '14px',
        }}>
          {menu.cover_url && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
            }} />
          )}

          <div style={{
            position: 'absolute', top: '12px', left: '12px', right: '12px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            zIndex: 5,
          }}>
            {/* Contact icons (left side) */}
            <div style={{ display: 'flex', gap: '6px' }}>
              {contactInfo.phone && (
                <a
                  href={`tel:${contactInfo.phone}`}
                  style={glassButtonStyle}
                  title="Call"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </a>
              )}
              {contactInfo.whatsapp && (
                <a
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank" rel="noreferrer"
                  style={glassButtonStyle}
                  title="WhatsApp"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
                  </svg>
                </a>
              )}
              {contactInfo.address && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(contactInfo.address)}`}
                  target="_blank" rel="noreferrer"
                  style={glassButtonStyle}
                  title={contactInfo.address}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </a>
              )}
              {contactInfo.instagram && (
                <a
                  href={contactInfo.instagram.startsWith('http') ? contactInfo.instagram : `https://instagram.com/${contactInfo.instagram.replace('@', '')}`}
                  target="_blank" rel="noreferrer"
                  style={glassButtonStyle}
                  title="Instagram"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>
              )}
              {contactInfo.facebook && (
                <a
                  href={contactInfo.facebook.startsWith('http') ? contactInfo.facebook : `https://facebook.com/${contactInfo.facebook.replace('@', '')}`}
                  target="_blank" rel="noreferrer"
                  style={glassButtonStyle}
                  title="Facebook"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                  </svg>
                </a>
              )}
              {contactInfo.twitter && (
                <a
                  href={contactInfo.twitter.startsWith('http') ? contactInfo.twitter : `https://x.com/${contactInfo.twitter.replace('@', '')}`}
                  target="_blank" rel="noreferrer"
                  style={glassButtonStyle}
                  title="X / Twitter"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 2h3l-7.5 8.5L22 22h-6.5l-5-6.5L4.5 22H2l8-9-7.5-11h6.5l4.5 6z" />
                  </svg>
                </a>
              )}
              {contactInfo.tiktok && (
                <a
                  href={contactInfo.tiktok.startsWith('http') ? contactInfo.tiktok : `https://tiktok.com/@${contactInfo.tiktok.replace('@', '')}`}
                  target="_blank" rel="noreferrer"
                  style={glassButtonStyle}
                  title="TikTok"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12a4 4 0 104 4V4a5 5 0 005 5" />
                  </svg>
                </a>
              )}
              {contactInfo.website && (
                <a
                  href={contactInfo.website.startsWith('http') ? contactInfo.website : `https://${contactInfo.website}`}
                  target="_blank" rel="noreferrer"
                  style={glassButtonStyle}
                  title="Website"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                  </svg>
                </a>
              )}
            </div>

            {/* Language toggle (right side) */}
            {supportedLocales.length > 1 && (
              <button
                onClick={() => {
                  const idx = supportedLocales.indexOf(locale);
                  setLocale(supportedLocales[(idx + 1) % supportedLocales.length]);
                }}
                style={{
                  ...glassButtonStyle,
                  width: 'auto',
                  height: '32px',
                  padding: '0 11px',
                  borderRadius: '999px',
                  display: 'inline-flex',
                  gap: '5px',
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                </svg>
                {locale === 'ar' ? 'العربية' : locale === 'en' ? 'English' : locale.toUpperCase()}
              </button>
            )}
          </div>

          <div style={{
            position: 'absolute', bottom: '14px',
            left: '14px', right: '14px',
            display: 'flex', alignItems: 'flex-end', gap: '12px',
            color: 'white', zIndex: 5,
          }}>
            {menu.logo_url && (
              <div style={{
                width: '52px', height: '52px',
                borderRadius: '12px', flexShrink: 0,
                background: `center/cover url("${menu.logo_url}") white`,
                boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
              }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '18px', fontWeight: 700, letterSpacing: '-0.3px',
                marginBottom: '2px',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                fontFamily: headerFontFamily,
              }}>{restaurantName}</div>
              {tagline && (
                <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '2px' }}>{tagline}</div>
              )}
              {openStatus && (
                <div style={{
                  display: 'flex', gap: '8px', fontSize: '11px',
                  alignItems: 'center', opacity: 0.95, flexWrap: 'wrap',
                }}>
                  <span style={{
                    background: openStatus.isOpen ? 'rgba(34, 197, 94, 0.9)' : 'rgba(220, 38, 38, 0.9)',
                    padding: '2px 8px', borderRadius: '999px',
                    fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                  }}>
                    <DotIcon size={6} />
                    {openStatus.label}
                  </span>
                  {openStatus.todayHours && (
                    <span style={{ fontSize: '11px', opacity: 0.85 }}>
                      Today: {openStatus.todayHours}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PDF menu format */}
        {menu.menu_format === 'pdf' && (() => {
          const pages = Array.isArray(menu.pdf_pages) ? menu.pdf_pages : [];
          const hasPages = pages.length > 0;
          // Only use the raw PdfViewer if pdf_url is a proper path (not just a legacy filename)
          const hasUsablePdfUrl = typeof menu.pdf_url === 'string' &&
            (menu.pdf_url.startsWith('/uploads/') || menu.pdf_url.startsWith('http'));
          const showAds = AD_SLOT_FOOTER && (!menu.owner_plan || menu.owner_plan === 'free');
          const showTopAd = AD_SLOT_HEADER && (!menu.owner_plan || menu.owner_plan === 'free');
          return (
            <div style={{ padding: '16px' }}>
              {/* Top ad — above the PDF viewer, free menus only */}
              {showTopAd && (
                <div style={{ marginBottom: '12px' }}>
                  <MenuAd slot={AD_SLOT_HEADER} format="fluid" layout="in-article" minHeight={120} />
                </div>
              )}
              {hasPages ? (
                <PdfPagesViewer pages={pages} primaryColor={primaryColor} />
              ) : hasUsablePdfUrl ? (
                <PdfViewer
                  url={menu.pdf_url}
                  primaryColor={primaryColor}
                  maxHeight="calc(100vh - 280px)"
                />
              ) : (
                <div style={{
                  padding: '40px 20px',
                  background: '#FAFAFB',
                  border: '1px dashed #E4E4E7',
                  borderRadius: '12px',
                  textAlign: 'center',
                  color: '#64748B',
                  fontSize: '13px',
                }}>
                  The owner hasn't uploaded the menu PDF yet. Please check back soon.
                </div>
              )}

              {/* Ad for free-plan PDF menus — shown below the viewer */}
              {showAds && (
                <MenuAd slot={AD_SLOT_FOOTER} format="auto" minHeight={100} />
              )}
            </div>
          );
        })()}

        {/* Digital menu (sections + items) */}
        {menu.menu_format !== 'pdf' && (
        <>
        <div style={{ padding: '16px 16px 0' }}>
          <div style={{
            background: '#FAFAFB',
            border: '1px solid #E4E4E7',
            borderRadius: '12px',
            padding: '11px 14px',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ color: '#94A3B8', display: 'inline-flex' }}><SearchIcon size={14} /></span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={locale === 'ar' ? 'ابحث عن طبق...' : 'Search dishes...'}
              style={{
                border: 'none', background: 'transparent', outline: 'none',
                flex: 1, fontSize: '13px', color: '#0F172A',
                fontFamily: 'inherit',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  color: '#94A3B8', fontSize: '14px',
                  cursor: 'pointer', background: 'none', border: 'none',
                }}
              >×</button>
            )}
          </div>
        </div>

        {!search && filteredSections.length > 1 && (
          <div
            ref={categoriesRef}
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderBottom: '1px solid #F1F5F9',
              padding: '12px 16px',
              display: 'flex', gap: '6px', overflowX: 'auto',
              scrollbarWidth: 'none', msOverflowStyle: 'none',
            }}
          >
            {filteredSections.map(section => {
              const sectionName = getText(section.name, locale);
              const isActive = activeSectionId === section.id;
              return (
                <button
                  key={section.id}
                  data-section-id={section.id}
                  onClick={() => scrollToSection(section.id)}
                  style={{
                    padding: '7px 14px',
                    background: isActive ? primaryColor : 'white',
                    color: isActive ? 'white' : '#475569',
                    border: `1px solid ${isActive ? primaryColor : '#E4E4E7'}`,
                    borderRadius: '999px',
                    fontSize: '12px', fontWeight: 500,
                    whiteSpace: 'nowrap', cursor: 'pointer',
                    boxShadow: isActive ? `0 4px 12px ${primaryColor}40` : 'none',
                    fontFamily: 'inherit',
                    transition: 'all 0.15s',
                    flexShrink: 0,
                  }}
                >{sectionName}</button>
              );
            })}
          </div>
        )}

        {/* Top ad — right under the banner / category pills. Free menus only. */}
        {AD_SLOT_HEADER && filteredSections.length > 0 && (!menu.owner_plan || menu.owner_plan === 'free') && (
          <div style={{ padding: '12px 14px 0' }}>
            <MenuAd slot={AD_SLOT_HEADER} format="fluid" layout="in-article" minHeight={120} />
          </div>
        )}

        <div style={{ padding: '14px', paddingBottom: '40px' }}>
          {filteredSections.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94A3B8' }}>
              {search ? 'No items match your search' : 'No menu items available'}
            </div>
          )}

          {filteredSections.map((section, sIndex) => {
            const sectionName = getText(section.name, locale);
            const sectionDesc = getText(section.description, locale);

            return (
              <Fragment key={section.id}>
              {/* Insert an in-feed ad after the 2nd section — only on free menus.
                  Paid plan owners (standard/pro) get an ad-free experience. */}
              {sIndex === 2 && filteredSections.length >= 4 && AD_SLOT_HEADER && (!menu.owner_plan || menu.owner_plan === 'free') && (
                <MenuAd slot={AD_SLOT_HEADER} format="fluid" layout="in-article" minHeight={120} />
              )}
              <div id={`sec-${section.id}`} style={{ marginBottom: '24px' }}>
                <div style={{
                  marginBottom: '10px',
                  ...(sectionBg ? {
                    background: sectionBg,
                    padding: '12px 14px',
                    borderRadius: '10px',
                  } : {}),
                }}>
                  <div style={{
                    fontSize: '18px', fontWeight: 700,
                    letterSpacing: '-0.3px', marginBottom: sectionDesc ? '2px' : '0',
                    fontFamily: headerFontFamily,
                    color: sectionFg || undefined,
                  }}>{sectionName}</div>
                  {sectionDesc && (
                    <div style={{
                      fontSize: '12px',
                      color: sectionFg ? `${sectionFg}aa` : '#64748B',
                    }}>{sectionDesc}</div>
                  )}
                </div>

                {(() => {
                  const isGrid = (menu.items_layout || 'grid') !== 'list';
                  return (
                <div style={isGrid ? {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '10px',
                } : undefined}>
                  {(section.items || []).map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      locale={locale}
                      primaryColor={primaryColor}
                      accentColor={accentColor}
                      currency={menu.currency}
                      isPopular={popularItemIds.has(item.id)}
                      orderingEnabled={menu.ordering_enabled}
                      compact={isGrid}
                      onClick={() => setSelectedItem(item)}
                      onToggleLike={() => handleToggleLike(item.id)}
                      onAddToCart={() => addToCart(item)}
                    />
                  ))}
                </div>
                  );
                })()}
              </div>
              </Fragment>
            );
          })}

          {/* Bottom ad after all sections — free menus only */}
          {AD_SLOT_FOOTER && (!menu.owner_plan || menu.owner_plan === 'free') && (
            <MenuAd slot={AD_SLOT_FOOTER} format="auto" minHeight={100} />
          )}
        </div>
        </>
        )}

        {/* Powered by footer */}
        <div style={{
          textAlign: 'center',
          padding: '20px 16px 28px',
          fontSize: '11px',
          color: '#94A3B8',
          borderTop: '1px solid #F1F5F9',
        }}>
          Powered by Almenu
        </div>
      </div>

      {/* Floating "back to top" button — hidden on PDF menus to avoid clashing
          with the zoom controls that sit in the same corner */}
      {menu.menu_format !== 'pdf' && (
      <button
        onClick={scrollToTop}
        aria-label="Back to top"
        style={{
          position: 'fixed',
          bottom: showScrollTop ? (cartCount > 0 ? '80px' : '20px') : '-60px',
          right: '20px',
          width: '40px', height: '40px',
          borderRadius: '50%',
          background: 'white',
          color: primaryColor,
          border: `1px solid ${primaryColor}40`,
          boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: showScrollTop ? 1 : 0,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 50,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      </button>
      )}

      {/* Floating cart button */}
      {menu.ordering_enabled && cartCount > 0 && (
        <button
          onClick={() => setShowCart(true)}
          aria-label="View cart"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 20px',
            borderRadius: '999px',
            background: primaryColor,
            color: 'white',
            border: 'none',
            boxShadow: `0 8px 20px ${primaryColor}50`,
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '14px',
            fontSize: '13px', fontWeight: 700,
            fontFamily: 'inherit',
            zIndex: 50,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{
            background: 'rgba(255,255,255,0.25)',
            borderRadius: '50%',
            width: '22px', height: '22px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', flexShrink: 0,
          }}>{cartCount}</span>
          <span style={{ whiteSpace: 'nowrap' }}>View order</span>
          <span style={{ width: '1px', height: '14px', background: 'rgba(255,255,255,0.35)' }} />
          <span style={{ whiteSpace: 'nowrap' }}>{menu.currency || '$'} {cartTotal.toFixed(2)}</span>
        </button>
      )}

      {/* Cart drawer */}
      {menu.ordering_enabled && (
        <CartDrawer
          isOpen={showCart}
          onClose={() => setShowCart(false)}
          items={cart}
          menu={menu}
          locale={locale}
          onUpdateQty={updateCartQty}
          onRemove={removeFromCart}
          onClearCart={clearCart}
        />
      )}

      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          locale={locale}
          primaryColor={primaryColor}
          currency={menu.currency}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}

// PDF as pre-rendered images — paginated viewer with zoom controls.
function PdfPagesViewer({ pages, primaryColor }) {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(null);
  const total = pages.length;
  const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 2];
  const go = (delta) => setIndex(i => Math.max(0, Math.min(total - 1, i + delta)));
  const zoomIn = () => setZoom(z => ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, ZOOM_STEPS.indexOf(z) + 1)] || 2);
  const zoomOut = () => setZoom(z => ZOOM_STEPS[Math.max(0, ZOOM_STEPS.indexOf(z) - 1)] || 0.75);
  const resetZoom = () => setZoom(1);

  // Reset zoom when changing pages so each page starts fitted to width
  useEffect(() => { setZoom(1); }, [index]);

  // Keyboard nav for desktop
  useEffect(() => {
    const onKey = (e) => {
      if (fullscreen !== null) return;
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen, total]);

  return (
    <div style={{ position: 'relative' }}>
      {/* Combined toolbar: page nav + zoom controls, all at the top */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '8px', flexWrap: 'wrap', marginBottom: '10px',
      }}>
        {/* Page navigation pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          background: 'white',
          border: '1px solid #E4E4E7',
          borderRadius: '999px',
          overflow: 'hidden',
        }}>
          <ToolbarBtn onClick={() => go(-1)} disabled={index === 0} aria-label="Previous page">‹</ToolbarBtn>
          <div style={{
            fontSize: '12px', fontWeight: 600,
            color: 'var(--text-2, #64748B)',
            padding: '0 10px', minWidth: '78px', textAlign: 'center',
            whiteSpace: 'nowrap',
          }}>Page {index + 1} of {total}</div>
          <ToolbarBtn onClick={() => go(1)} disabled={index >= total - 1} aria-label="Next page">›</ToolbarBtn>
        </div>

        {/* Zoom pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          background: 'white',
          border: '1px solid #E4E4E7',
          borderRadius: '999px',
          overflow: 'hidden',
        }}>
          <ToolbarBtn onClick={zoomOut} disabled={zoom <= ZOOM_STEPS[0]} aria-label="Zoom out">−</ToolbarBtn>
          <button
            onClick={resetZoom}
            style={{
              border: 'none', background: 'transparent',
              padding: '0 10px', fontSize: '11px', fontWeight: 600,
              color: 'var(--text-2, #64748B)', cursor: 'pointer',
              fontFamily: 'inherit', minWidth: '46px',
            }}
          >{Math.round(zoom * 100)}%</button>
          <ToolbarBtn onClick={zoomIn} disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} aria-label="Zoom in">+</ToolbarBtn>
        </div>
      </div>

      {/* Image — scrolls horizontally when zoomed; click-and-drag pans on desktop */}
      <DraggableImage
        src={pages[index]}
        alt={`Menu page ${index + 1}`}
        zoom={zoom}
        onClick={() => setFullscreen(index)}
      />

      {/* Fullscreen lightbox */}
      {fullscreen !== null && (
        <FullscreenPage
          pages={pages}
          startIndex={fullscreen}
          primaryColor={primaryColor}
          onClose={() => setFullscreen(null)}
        />
      )}
    </div>
  );
}

// Image with click-and-drag horizontal panning when zoomed in.
// A genuine click (no significant mouse movement) opens the fullscreen lightbox.
function DraggableImage({ src, alt, zoom, onClick }) {
  const containerRef = useRef(null);
  const dragRef = useRef({ down: false, startX: 0, scrollStart: 0, moved: 0 });

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    const el = containerRef.current;
    if (!el) return;
    dragRef.current = {
      down: true,
      startX: e.pageX,
      scrollStart: el.scrollLeft,
      moved: 0,
    };
  };

  const handleMouseMove = (e) => {
    if (!dragRef.current.down) return;
    const el = containerRef.current;
    if (!el) return;
    const dx = e.pageX - dragRef.current.startX;
    el.scrollLeft = dragRef.current.scrollStart - dx;
    dragRef.current.moved = Math.max(dragRef.current.moved, Math.abs(dx));
  };

  const stopDrag = () => { dragRef.current.down = false; };

  const handleClick = (e) => {
    // Skip the click→fullscreen if the user actually dragged
    if (dragRef.current.moved > 5) {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current.moved = 0;
      return;
    }
    onClick?.();
  };

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      style={{
        overflowX: zoom > 1 ? 'auto' : 'visible',
        cursor: zoom > 1 ? (dragRef.current.down ? 'grabbing' : 'grab') : 'zoom-in',
      }}
    >
      <img
        src={src}
        alt={alt}
        onClick={handleClick}
        draggable={false}
        style={{
          width: `${zoom * 100}%`,
          maxWidth: zoom > 1 ? 'none' : '100%',
          display: 'block',
          borderRadius: '8px',
          border: '1px solid #E4E4E7',
          background: 'white',
          userSelect: 'none',
          transition: 'width 0.2s ease',
        }}
      />
    </div>
  );
}

function ToolbarBtn({ children, ...props }) {
  return (
    <button
      {...props}
      style={{
        width: '34px', height: '34px',
        border: 'none', background: 'transparent',
        fontSize: '17px', fontWeight: 600,
        color: props.disabled ? '#CBD5E1' : '#0F172A',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >{children}</button>
  );
}


function FullscreenPage({ pages, startIndex, primaryColor, onClose }) {
  const [index, setIndex] = useState(startIndex);

  // ESC to close, arrow keys to navigate
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setIndex(i => Math.min(pages.length - 1, i + 1));
      else if (e.key === 'ArrowLeft') setIndex(i => Math.max(0, i - 1));
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, pages.length]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        touchAction: 'pinch-zoom',
      }}
    >
      <img
        src={pages[index]}
        alt={`Menu page ${index + 1}`}
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '100%', maxHeight: '100%',
          objectFit: 'contain',
          touchAction: 'pinch-zoom',
        }}
      />

      {/* Page counter */}
      <div style={{
        position: 'fixed', top: '14px', left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(255,255,255,0.92)',
        padding: '5px 14px', borderRadius: '999px',
        fontSize: '12px', fontWeight: 600,
        color: '#0F172A',
      }}>
        {index + 1} / {pages.length}
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'fixed', top: '14px', right: '14px',
          width: '38px', height: '38px',
          background: 'rgba(255,255,255,0.92)',
          border: 'none', borderRadius: '50%',
          fontSize: '20px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'inherit',
        }}
      >×</button>

      {/* Prev / next */}
      {index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIndex(i => i - 1); }}
          aria-label="Previous"
          style={{
            position: 'fixed', left: '12px', top: '50%',
            transform: 'translateY(-50%)',
            width: '44px', height: '44px',
            background: 'rgba(255,255,255,0.92)',
            border: 'none', borderRadius: '50%',
            fontSize: '20px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'inherit',
          }}
        >‹</button>
      )}
      {index < pages.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIndex(i => i + 1); }}
          aria-label="Next"
          style={{
            position: 'fixed', right: '12px', top: '50%',
            transform: 'translateY(-50%)',
            width: '44px', height: '44px',
            background: 'rgba(255,255,255,0.92)',
            border: 'none', borderRadius: '50%',
            fontSize: '20px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'inherit',
          }}
        >›</button>
      )}
    </div>
  );
}

function ItemCard({ item, locale, primaryColor, accentColor, currency, isPopular, orderingEnabled, compact, onClick, onToggleLike, onAddToCart }) {
  // Use the brand color for prices to keep the menu visually unified
  const priceColor = primaryColor;
  const name = getText(item.name, locale);
  const desc = getText(item.description, locale);
  const imageUrl = item.imageUrl || item.image_url;
  const likesCount = item.likesCount || 0;
  const isLiked = item.isLiked;

  const handleLikeClick = (e) => {
    e.stopPropagation();
    onToggleLike?.();
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    onAddToCart?.();
  };

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: compact ? 'column' : 'row',
        gap: compact ? '8px' : '12px',
        background: 'white',
        border: '1px solid #E4E4E7',
        borderRadius: '12px',
        padding: compact ? '10px' : '12px',
        marginBottom: compact ? 0 : '8px',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
        position: 'relative',
        height: '100%',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 4px 12px ${primaryColor}15`; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Image with badges — only rendered when an image exists */}
      {imageUrl && (
        <div style={{
          width: compact ? '100%' : '88px',
          height: compact ? '110px' : '88px',
          borderRadius: '8px',
          flexShrink: 0,
          background: `center/cover url("${imageUrl}")`,
          position: 'relative',
        }}>
          {/* Top-left badge: Popular or New */}
          {isPopular && (
            <div style={{
              position: 'absolute', top: '5px', left: '5px',
              background: '#DC2626', color: 'white',
              padding: '3px 7px', borderRadius: '999px',
              fontSize: '9px', fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: '3px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/>
              </svg>
              POPULAR
            </div>
          )}
          {!isPopular && item.isNew && (
            <div style={{
              position: 'absolute', top: '5px', left: '5px',
              background: primaryColor, color: 'white',
              padding: '3px 8px', borderRadius: '999px',
              fontSize: '9px', fontWeight: 700,
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}>NEW</div>
          )}

          {/* Top-right: Like button (on image) */}
          <button
            onClick={handleLikeClick}
            aria-label={isLiked ? 'Unlike' : 'Like'}
            style={{
              position: 'absolute', top: '5px', right: '5px',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(8px)',
              border: 'none',
              borderRadius: '999px',
              padding: '4px 8px',
              display: 'flex', alignItems: 'center', gap: '3px',
              cursor: 'pointer',
              fontSize: '10px', fontWeight: 700,
              color: isLiked ? '#DC2626' : '#475569',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              transition: 'transform 0.15s',
            }}
            onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
            onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
            </svg>
            {likesCount > 0 && <span>{likesCount}</span>}
          </button>
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '6px',
          marginBottom: '3px',
        }}>
          <div style={{
            fontSize: '14px', fontWeight: 600,
            letterSpacing: '-0.2px', flex: 1, minWidth: 0,
          }}>{name}</div>
          {/* Inline badges (only when no image — image-version shows them on the photo) */}
          {!imageUrl && isPopular && (
            <span style={{
              fontSize: '9px', fontWeight: 700,
              padding: '2px 7px', borderRadius: '999px',
              background: '#FEE2E2', color: '#DC2626',
              flexShrink: 0,
            }}>POPULAR</span>
          )}
          {!imageUrl && !isPopular && item.isNew && (
            <span style={{
              fontSize: '9px', fontWeight: 700,
              padding: '2px 7px', borderRadius: '999px',
              background: `${primaryColor}15`, color: primaryColor,
              flexShrink: 0,
            }}>NEW</span>
          )}
        </div>

        {desc && (
          <div style={{
            fontSize: '11px', color: '#475569', lineHeight: 1.5,
            marginBottom: '6px',
            display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>{desc}</div>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 'auto', gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: priceColor }}>
              {item.price ? `${currency || '$'} ${item.price}` : ''}
            </div>
            {item.isVegetarian && (
              <span style={{
                fontSize: '9px', padding: '2px 6px',
                background: '#DCFCE7', color: '#15803D',
                borderRadius: '5px', fontWeight: 700,
              }}>VEG</span>
            )}
            {item.isVegan && (
              <span style={{
                fontSize: '9px', padding: '2px 6px',
                background: '#DCFCE7', color: '#15803D',
                borderRadius: '5px', fontWeight: 700,
              }}>VEGAN</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {!imageUrl && (
              <button
                onClick={handleLikeClick}
                aria-label={isLiked ? 'Unlike' : 'Like'}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '4px',
                  display: 'flex', alignItems: 'center', gap: '3px',
                  cursor: 'pointer',
                  fontSize: '11px', fontWeight: 700,
                  color: isLiked ? '#DC2626' : '#94A3B8',
                  transition: 'transform 0.15s',
                }}
                onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
                onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                </svg>
                {likesCount > 0 && <span>{likesCount}</span>}
              </button>
            )}
            {orderingEnabled && item.price && (
              <button
                onClick={handleAddClick}
                aria-label="Add to cart"
                style={{
                  width: '24px', height: '24px',
                  borderRadius: '999px',
                  background: primaryColor,
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 2px 6px ${primaryColor}40`,
                  flexShrink: 0,
                  transition: 'transform 0.15s',
                  lineHeight: 1,
                }}
                onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
                onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >+</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ItemDetailModal({ item, locale, primaryColor, currency, onClose }) {
  const name = getText(item.name, locale);
  const desc = getText(item.description, locale);
  const imageUrl = item.imageUrl || item.image_url;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15,23,42,0.6)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white',
          width: '100%', maxWidth: '480px',
          maxHeight: '90vh', overflowY: 'auto',
          borderRadius: '24px 24px 0 0',
          padding: '0',
          animation: 'slideUp 0.25s ease-out',
        }}
      >
        <div style={{
          height: '260px',
          background: imageUrl
            ? `center/cover url("${imageUrl}")`
            : 'linear-gradient(135deg, #DDD6FE 0%, #A78BFA 100%)',
          position: 'relative',
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              width: '36px', height: '36px',
              background: 'rgba(255,255,255,0.95)',
              border: 'none', borderRadius: '50%',
              fontSize: '18px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
            }}
          >×</button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            gap: '12px', marginBottom: '8px',
          }}>
            <h2 style={{
              fontSize: '22px', fontWeight: 700,
              letterSpacing: '-0.4px', lineHeight: 1.2,
            }}>{name}</h2>
            <div style={{
              fontSize: '20px', fontWeight: 700,
              color: primaryColor, whiteSpace: 'nowrap',
            }}>{item.price ? `${currency || '$'} ${item.price}` : ''}</div>
          </div>

          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
            {item.prepTimeMin && (
              <div style={{
                fontSize: '11px', padding: '5px 10px',
                background: '#FAFAFB', borderRadius: '999px',
                color: '#475569', fontWeight: 500,
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}>
                <ClockIcon size={12} />
                {item.prepTimeMin} min
              </div>
            )}
            {item.calories && (
              <div style={{
                fontSize: '11px', padding: '5px 10px',
                background: '#FAFAFB', borderRadius: '999px',
                color: '#475569', fontWeight: 500,
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}>
                <FlameIcon size={12} />
                {item.calories} cal
              </div>
            )}
            {item.isVegetarian && (
              <div style={{
                fontSize: '11px', padding: '5px 10px',
                background: '#DCFCE7', borderRadius: '999px',
                color: '#15803D', fontWeight: 600,
              }}>Vegetarian</div>
            )}
            {item.isVegan && (
              <div style={{
                fontSize: '11px', padding: '5px 10px',
                background: '#DCFCE7', borderRadius: '999px',
                color: '#15803D', fontWeight: 600,
              }}>Vegan</div>
            )}
            {item.isGlutenFree && (
              <div style={{
                fontSize: '11px', padding: '5px 10px',
                background: '#FFEDD5', borderRadius: '999px',
                color: '#C2410C', fontWeight: 600,
              }}>Gluten-free</div>
            )}
          </div>

          {desc && (
            <p style={{
              fontSize: '14px', color: '#475569',
              lineHeight: 1.7, marginBottom: '20px',
            }}>{desc}</p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
